import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography, Paper, Stepper as MuiStepper, Step as MuiStep, StepLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const Stepper = ({ activeStep, children }) => {

    const { user } = useSelector((state) => state.user);
    const { shippingInfo, cartItems } = useSelector((state) => state.cart);
    const theme = useTheme();
    const navigate = useNavigate();

    const stepRoutes = ['/cart', '/shipping', '/order/confirm', '/process/payment'];

    const handleStepClick = (index) => {
        if (activeStep > index) {
            if (index === 0 && user.name) {
                // Do not navigate back to login if already logged in
                return;
            }
            navigate(stepRoutes[index]);
        }
    };

    const address = `${shippingInfo.address}, ${shippingInfo.city}, ${shippingInfo.state} - ${shippingInfo.pincode}`;

    const steps = [
        {
            label: "LOGIN",
            desc: user.name ? <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{user.name} <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>{user.email}</Typography></Typography> : null
        },
        {
            label: "DELIVERY ADDRESS",
            desc: user.name ? <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{user.name} <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>{address}</Typography></Typography> : null
        },
        {
            label: "ORDER SUMMARY",
            desc: <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{cartItems.length} Item</Typography>
        },
        {
            label: "PAYMENT OPTIONS",
            desc: <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Paytm</Typography>
        }
    ]

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            {/* Stepper Header */}
            <MuiStepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, display: { xs: 'none', md: 'flex' } }}>
                {steps.map((step) => (
                    <MuiStep key={step.label}>
                        <StepLabel>{step.label}</StepLabel>
                    </MuiStep>
                ))}
            </MuiStepper>

            {/* Stepper Body */}
            {steps.map((step, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                    {activeStep === index ? (
                        // Active Step
                        <Paper
                            elevation={theme.palette.mode === 'dark' ? 0 : 2}
                            sx={{
                                p: 0,
                                bgcolor: 'background.paper',
                                border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: theme.palette.primary.main, color: 'primary.contrastText', p: 1.5 }}>
                                <Box sx={{
                                    width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: 'primary.contrastText', color: 'primary.main',
                                    borderRadius: '50%', mr: 1.5,
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{index + 1}</Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{step.label}</Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                {children}
                            </Box>
                        </Paper>
                    ) : (
                        // Inactive/Completed Step
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : theme.palette.grey[50],
                                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}`,
                                cursor: activeStep > index ? 'pointer' : 'default',
                                transition: 'border-color 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: activeStep > index ? theme.palette.primary.main : undefined,
                                },
                                borderRadius: 1,
                            }}
                            onClick={() => handleStepClick(index)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{
                                    width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: activeStep > index
                                        ? theme.palette.primary.main
                                        : (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]),
                                    color: activeStep > index
                                        ? theme.palette.primary.contrastText
                                        : (theme.palette.mode === 'dark' ? theme.palette.grey[400] : 'text.secondary'),
                                    borderRadius: '50%',
                                    mr: 1.5,
                                }}>
                                    {activeStep > index ? <CheckIcon sx={{ fontSize: 16 }} /> : <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{index + 1}</Typography>}
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                    {step.label}
                                </Typography>
                            </Box>
                            {activeStep > index && step.desc && (
                                <Box sx={{ pl: '48px', pt: 1 }}>
                                    {step.desc}
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default Stepper;